import React, { useState } from 'react';
import { Box, Typography, TextField, Button, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

/**
 * A component that displays a field value with an edit button that toggles edit mode
 */
const ButtonEditableField = ({
  value,
  onSave,
  displayComponent,
  displayStyles = {},
  displayContainerStyles = {},
  editButtonText,
  editButtonIcon = <EditIcon />,
  editButtonProps = {},
  confirmButtonText = 'Save',
  cancelButtonText = 'Cancel',
  confirmButtonIcon = <CheckIcon />,
  cancelButtonIcon = <CloseIcon />,
  inputProps = {},
  placeholder = 'Enter value',
  label = null,
  showButtons = true,
  buttonSize = 'small',
  buttonVariant = 'outlined',
  confirmButtonVariant = 'contained',
  cancelButtonVariant = 'outlined',
  vertical = false, // Whether to display button below text (vertical) or beside text (horizontal)
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStartEdit = () => {
    setEditValue(value);
    setError('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(value);
    setError('');
  };

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(editValue);
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <Box>
      {isEditing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              setError('');
            }}
            onKeyDown={handleKeyDown}
            error={Boolean(error)}
            helperText={error}
            autoFocus
            placeholder={placeholder}
            label={label}
            fullWidth
            disabled={isSubmitting}
            {...inputProps}
          />
          {showButtons && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={confirmButtonIcon}
                onClick={handleSave}
                disabled={isSubmitting}
                variant={confirmButtonVariant}
                size={buttonSize}
              >
                {confirmButtonText}
              </Button>
              <Button
                startIcon={cancelButtonIcon}
                onClick={handleCancelEdit}
                disabled={isSubmitting}
                variant={cancelButtonVariant}
                size={buttonSize}
              >
                {cancelButtonText}
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: vertical ? 'column' : 'row',
            alignItems: vertical ? 'flex-start' : 'center',
            justifyContent: vertical ? 'flex-start' : 'space-between',
            gap: vertical ? 0.5 : 1,
            width: '100%',
            ...displayContainerStyles
          }}
        >
          {displayComponent ? (
            displayComponent
          ) : (
            <Typography sx={{ 
              flex: vertical ? 'none' : 1,  // Take available space in horizontal layout
              ...displayStyles 
            }}>
              {value || placeholder}
            </Typography>
          )}
          {editButtonText ? (
            <Button
              startIcon={editButtonIcon}
              onClick={handleStartEdit}
              size={buttonSize}
              variant={buttonVariant}
              {...editButtonProps}
            >
              {editButtonText}
            </Button>
          ) : (
            <IconButton 
              size={buttonSize} 
              onClick={handleStartEdit}
              {...editButtonProps}
            >
              {editButtonIcon}
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ButtonEditableField; 